import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Modal } from 'react-bootstrap';
import LoadingSpinner from 'components/LoadingSpinner';
import DeleteSuccessModal from 'components/GroupFavoriteModals/Success';
import DeleteModal from 'components/GroupFavoriteModals/Confirmation';
import { favGroupsClient } from 'apollo';
import {
  showDeleteSelector,
  groupIdSelector,
  groupContextSelector
} from 'containers/Groups/selectors';
import {
  clientIdSelector,
  currentBrandSelector,
  isEnterpriseContextSelector
} from 'store/global/selectors';
import { setShowDelete } from 'containers/Groups/actions';
import {
  FetchFilterGroupsDocument as fetchGroupsQuery,
  FetchFilterGroupDocument as fetchGroupQuery,
  FetchFilterGroupQuery as fetchGroupResult,
  FetchFilterGroupQueryVariables as fetchGroupVars,
  DeleteFilterGroupDocument as deleteGroupQuery,
  DeleteFilterGroupMutation as deleteGroupResult,
  DeleteFilterGroupMutationVariables as deleteGroupVars
} from 'generated/favorites-graphql';

interface Props {
  groupId?: string;
  context: string;
  clientId: string;
  currentBrand: string;
  isEnterpriseContext: boolean;
  showDelete: boolean;
  onClose(): void;
}

const DeleteConfirmationModal: React.FC<Props> = ({
  groupId,
  context,
  clientId,
  currentBrand,
  isEnterpriseContext,
  showDelete,
  onClose
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [groupName, setGroupName] = useState('');

  const handleDelete = deleteGroupMutation => {
    setGroupName('');
    deleteGroupMutation({
      variables: {
        id: groupId
      }
    });
  };

  const updateGroupListQuery = (context: string, cache: any, data: any) => {
    const groupsProps = {
      query: fetchGroupsQuery,
      variables: {
        clientID: clientId,
        context: context,
        ...(isEnterpriseContext && { brand: currentBrand })
      }
    };
    const groups = cache.readQuery({
      ...groupsProps
    });
    cache.writeQuery({
      ...groupsProps,
      data: {
        fetchFilterGroups: groups.fetchFilterGroups.map(group => {
          if (group.id === data.data.deleteFilterGroup.id) {
            return null;
          }
          return group;
        })
      }
    });
  };

  const handleDeleteCompleted = () => {
    onClose();
    if (!showSuccess) {
      setShowSuccess(true);
    }
  };

  const [deleteGroupMutation, { loading: deleteLoading }] = useMutation<
    deleteGroupResult,
    deleteGroupVars
  >(deleteGroupQuery, {
    client: favGroupsClient as any,
    notifyOnNetworkStatusChange: true,
    update: (cache, data) => {
      updateGroupListQuery(context, cache, data);
    },
    onCompleted: () => {
      handleDeleteCompleted();
    }
  });

  const { data, loading } = useQuery<fetchGroupResult, fetchGroupVars>(fetchGroupQuery, {
    client: favGroupsClient as any,
    variables: { id: groupId },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => {
        onCloseSuccess();
      }, 5000);
    }
  });

  useEffect(() => {
    if (data) {
      setGroupName(data.fetchFilterGroup.name);
    }
  }, [data]);

  const onCloseSuccess = () => {
    if (showSuccess) {
      setShowSuccess(false);
    }
  };

  const details = data && data.fetchFilterGroup;

  const renderBody = () => {
    if (deleteLoading) {
      return (
        <div className="retrieving-container">
          <LoadingSpinner message="Removing group..." />
        </div>
      );
    }
    if (groupId && groupName !== '' && !loading) {
      return (
        <DeleteModal
          title="Delete Group"
          name={groupName}
          onClose={onClose}
          handleDelete={() => handleDelete(deleteGroupMutation)}
        />
      );
    }
    return (
      <div className="retrieving-container">
        <LoadingSpinner />
      </div>
    );
  };

  return (
    <>
      <Modal
        centered
        dialogClassName="delete-confirmation-modal"
        show={showDelete}
        onHide={onClose}
      >
        {renderBody()}
      </Modal>
      <Modal centered show={showSuccess} onHide={onCloseSuccess}>
        <DeleteSuccessModal name={details ? details.name : ''} />
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({
  clientId: clientIdSelector(state),
  groupId: groupIdSelector(state),
  context: groupContextSelector(state),
  currentBrand: currentBrandSelector(state),
  isEnterpriseContext: isEnterpriseContextSelector(state),
  showDelete: showDeleteSelector(state)
});

const mapDispatchToProps = dispatch => ({
  onClose: () => {
    dispatch(setShowDelete(false));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(DeleteConfirmationModal);
